/**
 * Localizes Judge.me custom review questions and answers.
 *
 * Judge.me/Translation Lab translates review titles and bodies, but leaves
 * custom-form values in the language in which the review was submitted. The
 * translation map is rendered from Shopify's locale files by the product
 * reviews section.
 */

(() => {
  const configSelector = '[data-review-custom-question-translations]';
  const customFieldSelector = '.jdgm-rev__cf-ans__title, .jdgm-rev__cf-ans__value';

  class ReviewQuestionTranslations extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;

      const config = this.querySelector(configSelector);
      if (!config) return;

      try {
        this.translations = new Map(
          JSON.parse(config.textContent).flatMap(({ sources, translation }) =>
            sources.map((source) => [source.trim(), translation])
          )
        );
      } catch (error) {
        console.error('Could not parse review question translations.', error);
        return;
      }

      // Judge.me may render its widget as a separate Shopify app block, so it
      // is not guaranteed to be a descendant of this configuration element.
      this.reviewRoot = document.body;
      this.translateCustomFields(this.reviewRoot);

      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              this.translateCustomFields(node);
            }
          });
        });
      });

      this.observer.observe(this.reviewRoot, { childList: true, subtree: true });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
    }

    translateCustomFields(root) {
      const elements = [];

      if (root.matches?.(customFieldSelector)) {
        elements.push(root);
      }

      elements.push(...(root.querySelectorAll?.(customFieldSelector) || []));

      elements.forEach((element) => {
        const renderedSource = element.textContent.trim();
        const hasTrailingColon = /:\s*$/.test(renderedSource);
        const source = renderedSource.replace(/:\s*$/, '');
        const translation = this.translations.get(source);

        if (translation) {
          const renderedTranslation = hasTrailingColon ? `${translation}:` : translation;

          if (renderedTranslation !== renderedSource) {
            element.textContent = renderedTranslation;
          }
        }
      });
    }
  }

  if (!customElements.get('review-question-translations')) {
    customElements.define('review-question-translations', ReviewQuestionTranslations);
  }
})();
